import React, { createContext, useContext, useState, useEffect } from 'react';
import { supabase } from '../lib/supabase';

const DataContext = createContext();

export const usePortfolioData = () => {
  const ctx = useContext(DataContext);
  if (!ctx) throw new Error('usePortfolioData must be used within DataProvider');
  return ctx;
};

export const DataProvider = ({ children }) => {
  const [experiences, setExperiences] = useState([]);
  const [skills, setSkills] = useState({ languages: [], databases: [], frameworks: [], devops: [] });
  const [education, setEducation] = useState([]);
  const [certifications, setCertifications] = useState([]);
  const [spokenLanguages, setSpokenLanguages] = useState([]);
  const [contactInfo, setContactInfo] = useState({ email: '', phone: '', linkedin: '', location: {} });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const fetchAll = async () => {
    setLoading(true);
    setError(null);
    try {
      const [expRes, subRes, clientsRes, skillsRes, eduRes, certRes, langRes, settingsRes] = await Promise.all([
        supabase.from('experiences').select('*').eq('is_active', true).order('sort_order'),
        supabase.from('experience_sub_projects').select('*').order('sort_order'),
        supabase.from('experience_clients').select('*').order('sort_order'),
        supabase.from('skills').select('*').eq('is_active', true).order('sort_order'),
        supabase.from('education').select('*').order('sort_order'),
        supabase.from('certifications').select('*').order('sort_order'),
        supabase.from('languages_spoken').select('*').order('sort_order'),
        supabase.from('site_settings').select('*')
      ]);

      if (expRes.error) { setError(expRes.error.message); setLoading(false); return; }

      const exps = (expRes.data || []).map(exp => ({
        ...exp,
        subProjects: (subRes.data || []).filter(sp => sp.experience_id === exp.id),
        clients: (clientsRes.data || []).filter(c => c.experience_id === exp.id)
      }));
      setExperiences(exps);

      const allSkills = skillsRes.data || [];
      setSkills({
        languages: allSkills.filter(s => s.category === 'languages'),
        databases: allSkills.filter(s => s.category === 'databases'),
        frameworks: allSkills.filter(s => s.category === 'frameworks'),
        devops: allSkills.filter(s => s.category === 'devops')
      });

      setEducation(eduRes.data || []);
      setCertifications(certRes.data || []);
      setSpokenLanguages(langRes.data || []);

      const contactSetting = (settingsRes.data || []).find(s => s.key === 'contact_info');
      if (contactSetting) setContactInfo(contactSetting.value);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchAll(); }, []);

  return (
    <DataContext.Provider value={{
      experiences, skills, education, certifications, spokenLanguages, contactInfo, loading, error, refetch: fetchAll
    }}>
      {children}
    </DataContext.Provider>
  );
};
